import { setAccordionState } from '../utils/dom.js';
import { normalizeSearchText, tokenizeSearchText } from '../utils/search.js';

const formatResultsCount = (element, visibleCount) => {
  if (!element) return;
  const template = element.dataset.resultsTemplate || '';
  if (template) {
    const withToken = template.replace('{count}', String(visibleCount));
    const withLeadingNumber = withToken.replace(/^\s*\d+/, String(visibleCount));
    element.textContent = withLeadingNumber;
    return;
  }
  element.textContent = `${visibleCount}`;
};

const initFaqSection = (root) => {
  const accordion = root.querySelector('.accordion');
  if (!accordion) return;

  const accordionItems = accordion.querySelectorAll('.accordion-item');
  const tabs = root.querySelectorAll('.filter-tab');
  const searchInputs = root.querySelectorAll('.filter-search-field');
  const clearButtons = root.querySelectorAll('.filter-clear-button');
  const resultsInfo = root.querySelector('.filter-results-info');
  const resultsCount = root.querySelector('.filter-results-count');
  const noResultsAlert = root.querySelector('.filter-no-results-alert');
  const resetButton = root.querySelector('.filter-reset-button');

  const setFaqTabState = (tab, isActive) => {
    tab.classList.toggle('active', isActive);
    tab.classList.toggle('btn-secondary', isActive);
    tab.classList.toggle('btn-secondary-outline', !isActive);
  };

  const clearActiveFaqTabs = () => {
    tabs.forEach((tab) => {
      setFaqTabState(tab, false);
    });
  };

  const clearFaqSearch = () => {
    searchInputs.forEach((input) => {
      if (input.value) {
        input.value = '';
      }
    });
    updateClearButtonVisibility();
    updateResultsDisplay();
  };

  const updateClearButtonVisibility = () => {
    searchInputs.forEach((input, index) => {
      const clearButton = clearButtons[index];
      if (clearButton) {
        clearButton.style.display = input.value ? 'flex' : 'none';
      }
    });
  };

  const updateResultsDisplay = () => {
    const visibleCount = Array.from(accordionItems).filter(item => !item.hidden).length;
    const hasSearch = Array.from(searchInputs).some(input => input.value.trim());
    const hasActiveCategory = accordion.dataset.activeCategory;

    formatResultsCount(resultsCount, visibleCount);

    if (resultsInfo) {
      resultsInfo.style.display = (hasSearch || hasActiveCategory) ? 'flex' : 'none';
    }

    if (noResultsAlert) {
      noResultsAlert.style.display = (hasSearch || hasActiveCategory) && visibleCount === 0 ? 'flex' : 'none';
    }
  };

  const applyFaqFilter = (category) => {
    accordion.dataset.activeCategory = category;
    accordionItems.forEach((item) => {
      const itemCategory = item.getAttribute('data-category');
      const shouldShow = itemCategory === category;
      item.hidden = !shouldShow;
      if (!shouldShow) {
        setAccordionState(item, false);
      }
    });
    updateResultsDisplay();
  };

  const selectFaqTab = (tab) => {
    const category = tab?.getAttribute('data-category');
    if (!category) return;
    clearFaqSearch();
    tabs.forEach((entry) => setFaqTabState(entry, entry === tab));
    applyFaqFilter(category);
  };

  const resetFilters = () => {
    clearFaqSearch();
    clearActiveFaqTabs();
    accordion.dataset.activeCategory = '';
    accordionItems.forEach((item) => {
      item.hidden = false;
    });
    if (tabs.length > 0) {
      const initialTab = Array.from(tabs).find((tab) => tab.classList.contains('active')) || tabs[0];
      selectFaqTab(initialTab);
    }
    updateResultsDisplay();
  };

  if (tabs.length > 0) {
    const initialTab = Array.from(tabs).find((tab) => tab.classList.contains('active')) || tabs[0];
    selectFaqTab(initialTab);

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        selectFaqTab(tab);
      });
    });
  }

  accordionItems.forEach((item) => {
    const question = item.getAttribute('data-question') || '';
    const answer = item.getAttribute('data-answer') || '';
    item.dataset.searchTokens = tokenizeSearchText(`${question} ${answer}`).join(' ');
  });

  const applySearchFilter = (query) => {
    const queryTokens = tokenizeSearchText(query);
    const activeCategory = accordion.dataset.activeCategory;

    accordionItems.forEach((item) => {
      const itemCategory = item.getAttribute('data-category');
      const matchesCategory = !activeCategory || itemCategory === activeCategory;
      const itemTokens = (item.dataset.searchTokens || '').split(' ').filter(Boolean);
      const itemTokenSet = new Set(itemTokens);
      const matchesQuery =
        queryTokens.length === 0 || queryTokens.every((token) => itemTokenSet.has(token));
      const shouldShow = matchesCategory && matchesQuery;
      item.hidden = !shouldShow;
      if (!shouldShow) {
        setAccordionState(item, false);
      }
    });
    updateResultsDisplay();
  };

  searchInputs.forEach((input, index) => {
    input.addEventListener('input', () => {
      updateClearButtonVisibility();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      accordion.dataset.activeCategory = '';
      clearActiveFaqTabs();
      applySearchFilter(input.value);
    });

    const clearButton = clearButtons[index];
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        input.value = '';
        updateClearButtonVisibility();
        accordion.dataset.activeCategory = '';
        clearActiveFaqTabs();
        applySearchFilter('');
      });
    }
  });

  if (resetButton) {
    resetButton.addEventListener('click', resetFilters);
  }

  updateResultsDisplay();
};

const initPressSection = (root) => {
  const cards = root.querySelectorAll('.card-list-item');
  if (cards.length === 0) return;

  const tabs = root.querySelectorAll('.filter-tab');
  const searchInputs = root.querySelectorAll('.filter-search-field');
  const clearButtons = root.querySelectorAll('.filter-clear-button');
  const resultsInfo = root.querySelector('.filter-results-info');
  const resultsCount = root.querySelector('.filter-results-count');
  const noResultsAlert = root.querySelector('.filter-no-results-alert');
  const resetButton = root.querySelector('.filter-reset-button');

  const setPressTabState = (tab, isActive) => {
    tab.classList.toggle('active', isActive);
    tab.classList.toggle('btn-secondary', isActive);
    tab.classList.toggle('btn-secondary-outline', !isActive);
  };

  const clearActiveTabs = () => {
    tabs.forEach((tab) => {
      setPressTabState(tab, false);
    });
  };

  const clearSearchInputs = () => {
    searchInputs.forEach((input) => {
      if (input.value) {
        input.value = '';
      }
    });
    updateClearButtonVisibility();
    updateResultsDisplay();
  };

  const updateClearButtonVisibility = () => {
    searchInputs.forEach((input, index) => {
      const clearButton = clearButtons[index];
      if (clearButton) {
        clearButton.style.display = input.value ? 'flex' : 'none';
      }
    });
  };

  const updateResultsDisplay = () => {
    const visibleCount = Array.from(cards).filter(card => !card.hidden).length;
    const hasSearch = Array.from(searchInputs).some(input => input.value.trim());
    const hasActiveCategory = root.dataset.activeCategory;

    formatResultsCount(resultsCount, visibleCount);

    if (resultsInfo) {
      resultsInfo.style.display = (hasSearch || hasActiveCategory) ? 'flex' : 'none';
    }

    if (noResultsAlert) {
      noResultsAlert.style.display = (hasSearch || hasActiveCategory) && visibleCount === 0 ? 'flex' : 'none';
    }
  };

  const applyCategoryFilter = (category) => {
    root.dataset.activeCategory = category;
    cards.forEach((card) => {
      const cardCategory = card.getAttribute('data-category');
      const shouldShow = !category || cardCategory === category;
      card.hidden = !shouldShow;
    });
    updateResultsDisplay();
  };

  const selectTab = (tab) => {
    const category = tab?.getAttribute('data-category');
    if (!category) return;
    clearSearchInputs();
    tabs.forEach((entry) => setPressTabState(entry, entry === tab));
    applyCategoryFilter(category);
  };

  const resetFilters = () => {
    clearSearchInputs();
    clearActiveTabs();
    root.dataset.activeCategory = '';
    cards.forEach((card) => {
      card.hidden = false;
    });
    if (tabs.length > 0) {
      const initialTab = Array.from(tabs).find((tab) => tab.classList.contains('active')) || tabs[0];
      selectTab(initialTab);
    }
    updateResultsDisplay();
  };

  if (tabs.length > 0) {
    const initialTab = Array.from(tabs).find((tab) => tab.classList.contains('active')) || tabs[0];
    selectTab(initialTab);

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        selectTab(tab);
      });
    });
  }

  cards.forEach((card) => {
    const title = card.querySelector('.card-list-title')?.textContent || '';
    const source = card.querySelector('.card-list-source')?.textContent || '';
    card.dataset.searchTokens = tokenizeSearchText(`${title} ${source}`).join(' ');
  });

  const applySearchFilter = (query) => {
    const queryTokens = tokenizeSearchText(query);
    const activeCategory = root.dataset.activeCategory;

    cards.forEach((card) => {
      const cardCategory = card.getAttribute('data-category');
      const matchesCategory = !activeCategory || cardCategory === activeCategory;
      const cardTokens = (card.dataset.searchTokens || '').split(' ').filter(Boolean);
      const cardTokenSet = new Set(cardTokens);
      const matchesQuery =
        queryTokens.length === 0 || queryTokens.every((token) => cardTokenSet.has(token));
      const shouldShow = matchesCategory && matchesQuery;
      card.hidden = !shouldShow;
    });
    updateResultsDisplay();
  };

  searchInputs.forEach((input, index) => {
    input.addEventListener('input', () => {
      updateClearButtonVisibility();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      root.dataset.activeCategory = '';
      clearActiveTabs();
      applySearchFilter(input.value);
    });

    const clearButton = clearButtons[index];
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        input.value = '';
        updateClearButtonVisibility();
        root.dataset.activeCategory = '';
        clearActiveTabs();
        applySearchFilter('');
      });
    }
  });

  if (resetButton) {
    resetButton.addEventListener('click', resetFilters);
  }

  updateResultsDisplay();
};

const initBlogSection = (root) => {
  const cards = Array.from(root.querySelectorAll('.blog-card-item'));
  if (cards.length === 0) return;

  const tabs = root.querySelectorAll('.blog-filter-tab');
  const searchInput = root.querySelector('.blog-search-field');
  const clearBtn = root.querySelector('.blog-search-clear-btn');
  const resultsInfo = root.querySelector('.blog-results-info');
  const resultsCount = root.querySelector('.blog-results-count');
  const noResultsAlert = root.querySelector('.blog-no-results-alert');
  const resetBtn = root.querySelector('.blog-filter-reset-btn');
  const loadMoreWrapper = root.querySelector('.blog-load-more-wrapper');
  const loadMoreBtn = root.querySelector('.blog-load-more-btn');

  const BLOG_PAGE_SIZE = 9;
  let visibleLimit = BLOG_PAGE_SIZE;

  const isMatch = (card) => card.dataset.match !== '0';

  const setMatches = (predicate) => {
    cards.forEach((card) => { card.dataset.match = predicate(card) ? '1' : '0'; });
  };

  // Reveals the first `visibleLimit` matching cards and hides the rest,
  // so the load more button has something left to reveal.
  const applyPaging = () => {
    let shown = 0;
    cards.forEach((card) => {
      const visible = isMatch(card) && shown < visibleLimit;
      if (visible) shown += 1;
      card.hidden = !visible;
    });
    if (loadMoreWrapper) {
      loadMoreWrapper.style.display = cards.filter(isMatch).length > shown ? 'flex' : 'none';
    }
  };

  const setBlogTabState = (tab, isActive) => {
    tab.classList.toggle('btn-secondary', isActive);
    tab.classList.toggle('btn-secondary-outline', !isActive);
  };

  const updateClearBtnVisibility = () => {
    if (clearBtn) clearBtn.style.display = searchInput?.value ? 'flex' : 'none';
  };

  const updateResultsDisplay = () => {
    const visibleCount = cards.filter(isMatch).length;
    const hasSearch = searchInput?.value.trim();
    const hasCategory = root.dataset.activeCategory;

    formatResultsCount(resultsCount, visibleCount);
    if (resultsInfo) resultsInfo.style.display = (hasSearch || hasCategory) ? 'flex' : 'none';
    if (noResultsAlert) {
      noResultsAlert.style.display = (hasSearch || hasCategory) && visibleCount === 0 ? 'flex' : 'none';
    }
  };

  const applyBlogCategoryFilter = (category) => {
    root.dataset.activeCategory = category;
    setMatches((card) => !category || card.getAttribute('data-category') === category);
    visibleLimit = BLOG_PAGE_SIZE;
    applyPaging();
    updateResultsDisplay();
  };

  const selectBlogTab = (tab) => {
    const category = tab.getAttribute('data-category') || '';
    if (searchInput) searchInput.value = '';
    updateClearBtnVisibility();
    tabs.forEach(t => setBlogTabState(t, t === tab));
    applyBlogCategoryFilter(category);
  };

  const applyBlogSearch = (query) => {
    const queryTokens = tokenizeSearchText(query);
    const activeCategory = root.dataset.activeCategory || '';
    setMatches((card) => {
      const matchesCategory = !activeCategory || card.getAttribute('data-category') === activeCategory;
      const cardTokenSet = new Set((card.dataset.searchTokens || '').split(' ').filter(Boolean));
      const matchesQuery = queryTokens.length === 0 || queryTokens.every(t => cardTokenSet.has(t));
      return matchesCategory && matchesQuery;
    });
    visibleLimit = BLOG_PAGE_SIZE;
    applyPaging();
    updateResultsDisplay();
  };

  const resetBlogFilters = () => {
    if (searchInput) searchInput.value = '';
    updateClearBtnVisibility();
    root.dataset.activeCategory = '';
    setMatches(() => true);
    visibleLimit = BLOG_PAGE_SIZE;
    applyPaging();
    const firstTab = tabs[0];
    if (firstTab) {
      tabs.forEach(t => setBlogTabState(t, t === firstTab));
    }
    updateResultsDisplay();
  };

  // Index card search tokens
  cards.forEach((card) => {
    const title = card.querySelector('.blog-card-title')?.textContent || '';
    card.dataset.searchTokens = tokenizeSearchText(title).join(' ');
  });

  // Wire up tab clicks
  tabs.forEach((tab) => tab.addEventListener('click', () => selectBlogTab(tab)));

  // Wire up search
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      updateClearBtnVisibility();
    });

    searchInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      root.dataset.activeCategory = '';
      tabs.forEach(t => setBlogTabState(t, false));
      applyBlogSearch(searchInput.value);
    });
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      if (searchInput) searchInput.value = '';
      updateClearBtnVisibility();
      root.dataset.activeCategory = '';
      tabs.forEach(t => setBlogTabState(t, false));
      applyBlogSearch('');
    });
  }

  if (resetBtn) resetBtn.addEventListener('click', resetBlogFilters);

  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', () => {
      visibleLimit += BLOG_PAGE_SIZE;
      applyPaging();
    });
  }

  // Activate first tab by default
  if (tabs.length > 0) {
    selectBlogTab(tabs[0]);
  } else {
    setMatches(() => true);
    applyPaging();
    updateResultsDisplay();
  }
};

export const initBlogSections = () => {
  const blogSections = document.querySelectorAll('.blog-page');
  blogSections.forEach((section) => initBlogSection(section));

};

export const initFaqSections = () => {
  const faqSections = document.querySelectorAll('.faq-section');
  faqSections.forEach((section) => initFaqSection(section));
};

export const initPressSections = () => {
  const pressSections = document.querySelectorAll('.press-page');
  pressSections.forEach((section) => initPressSection(section));
};

export const initLocationFilterModal = () => {
  const locationFilterButton = document.getElementById('locationFilterButton');
  const locationFilterModalOverlay = document.getElementById('locationFilterModalOverlay');
  const locationFilterModalClose = document.getElementById('locationFilterModalClose');
  const locationApplyButton = document.getElementById('locationApplyButton');
  const locationClearButton = document.getElementById('locationClearButton');
  const locationSearchField = document.getElementById('locationSearchField');
  const locationCitiesList = document.getElementById('locationCitiesList');
  const cityItems = locationCitiesList?.querySelectorAll('.location-city-item');

  if (!locationFilterButton || !locationFilterModalOverlay) return;

  let selectedCities = new Set(['istanbul']);

  const openLocationFilterModal = () => {
    locationFilterModalOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  };

  const closeLocationFilterModal = () => {
    locationFilterModalOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  const updateCitySelection = (cityId, isSelected) => {
    if (isSelected) {
      selectedCities.add(cityId);
    } else {
      selectedCities.delete(cityId);
    }

    cityItems?.forEach((item) => {
      const city = item.getAttribute('data-city');
      if (city === cityId) {
        item.classList.toggle('active', isSelected);
      }
    });
  };

  const syncCitySelection = () => {
    cityItems?.forEach((item) => {
      const city = item.getAttribute('data-city');
      if (city && selectedCities.has(city)) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  };

  const applyFilter = () => {
    console.log('Selected cities:', Array.from(selectedCities));
    closeLocationFilterModal();
  };

  const clearFilter = () => {
    selectedCities.clear();
    syncCitySelection();
    if (locationSearchField) {
      locationSearchField.value = '';
    }
    cityItems?.forEach((item) => {
      item.removeAttribute('hidden');
    });
  };

  const filterCities = (query) => {
    const normalizedQuery = normalizeSearchText(query);
    const queryTokens = tokenizeSearchText(query);

    cityItems?.forEach((item) => {
      const cityName = item.querySelector('.location-city-name')?.textContent || '';
      const normalizedCityName = normalizeSearchText(cityName);
      const cityTokens = tokenizeSearchText(cityName);
      const cityTokenSet = new Set(cityTokens);

      const matchesQuery = queryTokens.length === 0 ||
        queryTokens.every(token => cityTokenSet.has(token)) ||
        normalizedCityName.includes(normalizedQuery);

      if (matchesQuery) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', 'hidden');
      }
    });
  };

  locationFilterButton.addEventListener('click', openLocationFilterModal);

  if (locationFilterModalClose) {
    locationFilterModalClose.addEventListener('click', closeLocationFilterModal);
  }

  locationFilterModalOverlay.addEventListener('click', (event) => {
    if (event.target === locationFilterModalOverlay) {
      closeLocationFilterModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && locationFilterModalOverlay.classList.contains('is-open')) {
      closeLocationFilterModal();
    }
  });

  cityItems?.forEach((item) => {
    item.addEventListener('click', () => {
      const city = item.getAttribute('data-city');
      if (!city) return;

      const isActive = item.classList.contains('active');
      updateCitySelection(city, !isActive);
    });
  });

  if (locationApplyButton) {
    locationApplyButton.addEventListener('click', applyFilter);
  }

  if (locationClearButton) {
    locationClearButton.addEventListener('click', clearFilter);
  }

  if (locationSearchField) {
    locationSearchField.addEventListener('input', () => {
      filterCities(locationSearchField.value);
    });
  }

  syncCitySelection();
};
